import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import api from '../utils/api';
import { toast } from 'sonner';
import { ArrowLeft, Users, UserPlus, UserCheck, UserX, Shield, Clock, Mail, Key, Loader2 } from 'lucide-react';

const UsersSettings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [accessRequests, setAccessRequests] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [isTenantAdmin, setIsTenantAdmin] = useState(false);
  
  // Dialog states
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [showConvertDialog, setShowConvertDialog] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);
  const [approvePassword, setApprovePassword] = useState('');
  const [approveRole, setApproveRole] = useState('client_user');
  const [convertPassword, setConvertPassword] = useState('');
  const [convertRole, setConvertRole] = useState('client_user');
  const [processing, setProcessing] = useState(false);
  
  // Roles/Permissions states
  const [rolePermissions, setRolePermissions] = useState({});
  const [savingPermissions, setSavingPermissions] = useState(false);

  useEffect(() => {
    fetchData();
    fetchRolePermissions();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Get current user info
      const meRes = await api.get('/auth/me');
      setCurrentUser(meRes.data);
      setIsTenantAdmin(meRes.data.tenant_admin || false);

      // Get users
      const usersRes = await api.get('/users');
      setUsers(usersRes.data.users || []);

      // If tenant admin, get access requests
      if (meRes.data.tenant_admin) {
        try {
          const requestsRes = await api.get('/access-requests');
          setAccessRequests(requestsRes.data.requests || []);
        } catch (error) {
          console.error('Failed to load access requests:', error);
        }

        // Get all contacts to show convert option
        try {
          const clientsRes = await api.get('/clients');
          const allContacts = [];
          for (const client of clientsRes.data) {
            try {
              const contactsRes = await api.get(`/clients/${client.id}/contacts`);
              allContacts.push(...contactsRes.data.map(c => ({ ...c, client_name: client.company_name })));
            } catch (error) {
              console.error(`Failed to load contacts for client ${client.id}`);
            }
          }
          // Filter out contacts that are already users
          const userEmails = new Set((usersRes.data.users || []).map(u => u.email.toLowerCase()));
          setContacts(allContacts.filter(c => !userEmails.has(c.email.toLowerCase())));
        } catch (error) {
          console.error('Failed to load contacts:', error);
        }
      }
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const fetchRolePermissions = async () => {
    try {
      const res = await api.get('/roles/permissions');
      setRolePermissions(res.data.permissions || {});
    } catch (error) {
      console.error('Failed to load role permissions:', error);
    }
  };

  const handlePermissionChange = (role, module, action, value) => {
    setRolePermissions(prev => ({
      ...prev,
      [role]: {
        ...prev[role],
        [module]: {
          ...prev[role]?.[module],
          [action]: value
        }
      }
    }));
  };

  const handleSaveRolePermissions = async (role) => {
    setSavingPermissions(true);
    try {
      await api.put(`/roles/permissions/${role}`, {
        permissions: rolePermissions[role]
      });
      toast.success(`Permissions updated for ${getRoleLabel(role)}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update permissions');
    } finally {
      setSavingPermissions(false);
    }
  };

  const handleResetPermissions = async () => {
    if (!confirm('Are you sure you want to reset all role permissions to defaults? This cannot be undone.')) return;
    
    try {
      await api.post('/roles/permissions/reset');
      toast.success('All permissions reset to defaults');
      fetchRolePermissions();
    } catch (error) {
      toast.error('Failed to reset permissions');
    }
  };

  const getRoleLabel = (role) => {
    const roleMap = {
      'msp_admin': 'MSP Administrator',
      'msp_tech': 'MSP Technician',
      'client_manager': 'Client Manager',
      'client_user': 'Client User',
      'admin': 'Admin (Legacy)',
      'user': 'User (Legacy)'
    };
    return roleMap[role] || role;
  };

  const getRoleBadgeColor = (role) => {
    const colorMap = {
      'msp_admin': 'bg-purple-100 text-purple-800 border-purple-200',
      'msp_tech': 'bg-blue-100 text-blue-800 border-blue-200',
      'client_manager': 'bg-green-100 text-green-800 border-green-200',
      'client_user': 'bg-gray-100 text-gray-800 border-gray-200',
      'admin': 'bg-red-100 text-red-800 border-red-200',
      'user': 'bg-slate-100 text-slate-800 border-slate-200'
    };
    return colorMap[role] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await api.put(`/users/${userId}/role`, { role: newRole });
      toast.success('User role updated successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user role');
    }
  };

  const handleToggleUserStatus = async (userId, currentStatus) => {
    try {
      await api.put(`/users/${userId}/status`, { is_active: !currentStatus });
      toast.success(currentStatus ? 'User deactivated' : 'User activated');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user status');
    }
  };

  const handleApproveRequest = async () => {
    if (!approvePassword || approvePassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setProcessing(true);
    try {
      await api.post(`/access-requests/${selectedRequest.id}/approve`, {
        password: approvePassword,
        role: approveRole
      });
      toast.success('Access request approved successfully');
      setShowApproveDialog(false);
      setApprovePassword('');
      setApproveRole('client_user');
      setSelectedRequest(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve request');
    } finally {
      setProcessing(false);
    }
  };

  const handleRejectRequest = async (requestId) => {
    if (!confirm('Are you sure you want to reject this access request?')) return;

    try {
      await api.post(`/access-requests/${requestId}/reject`);
      toast.success('Access request rejected');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject request');
    }
  };

  const handleConvertContact = async () => {
    if (!convertPassword || convertPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setProcessing(true);
    try {
      await api.post(`/users/from-contact/${selectedContact.id}`, {
        password: convertPassword,
        role: convertRole
      });
      toast.success('Contact converted to user successfully');
      setShowConvertDialog(false);
      setConvertPassword('');
      setConvertRole('client_user');
      setSelectedContact(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to convert contact');
    } finally {
      setProcessing(false);
    }
  };

  const openApproveDialog = (request) => {
    setSelectedRequest(request);
    setApprovePassword('');
    setApproveRole('client_user');
    setShowApproveDialog(true);
  };

  const openConvertDialog = (contact) => {
    setSelectedContact(contact);
    setConvertPassword('');
    setConvertRole('client_user');
    setShowConvertDialog(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!isTenantAdmin) {
    return (
      <div>
        <div className="mb-6">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/settings')}
            className="border-slate-300"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Settings
          </Button>
        </div>
        <Card>
          <CardContent className="py-12 text-center">
            <Shield className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <h3 className="text-xl font-semibold text-slate-700 mb-2">Access Denied</h3>
            <p className="text-slate-500 mb-6">Only tenant administrators can access user management</p>
            <Button onClick={() => navigate('/settings')} variant="outline">
              Back to Settings
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/settings')}
            className="border-slate-300"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-purple-600 to-purple-700 rounded-xl flex items-center justify-center">
              <Users className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-800">User Management</h1>
              <p className="text-sm text-slate-500">Manage users and access requests</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl">
        <Tabs defaultValue="active" className="space-y-6">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="active">
              <UserCheck className="w-4 h-4 mr-2" />
              Active Users ({users.filter(u => u.is_active !== false).length})
            </TabsTrigger>
            <TabsTrigger value="pending">
              <Clock className="w-4 h-4 mr-2" />
              Pending Requests ({accessRequests.length})
            </TabsTrigger>
            <TabsTrigger value="contacts">
              <UserPlus className="w-4 h-4 mr-2" />
              Contacts ({contacts.length})
            </TabsTrigger>
            <TabsTrigger value="roles">
              <Shield className="w-4 h-4 mr-2" />
              Roles & Permissions
            </TabsTrigger>
          </TabsList>

          {/* Active Users Tab */}
          <TabsContent value="active" className="space-y-4">
            {users.filter(u => u.is_active !== false).length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Users className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p className="text-slate-500">No active users found</p>
                </CardContent>
              </Card>
            ) : (
              users.filter(u => u.is_active !== false).map((user) => (
                <Card key={user.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-4 flex-1">
                        <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                          <Users className="w-6 h-6 text-purple-600" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="text-lg font-semibold text-slate-800">{user.name}</h3>
                            {user.tenant_admin && (
                              <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">
                                <Shield className="w-3 h-3 mr-1" />
                                Tenant Admin
                              </Badge>
                            )}
                            <Badge className={getRoleBadgeColor(user.role)}>
                              {getRoleLabel(user.role)}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-slate-600">
                            <Mail className="w-4 h-4" />
                            {user.email}
                          </div>
                          {user.contact_id && (
                            <p className="text-xs text-slate-500 mt-1">
                              Converted from contact
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Select
                          value={user.role}
                          onValueChange={(newRole) => handleRoleChange(user.id, newRole)}
                          disabled={user.id === currentUser?.id}
                        >
                          <SelectTrigger className="w-48">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="msp_admin">MSP Administrator</SelectItem>
                            <SelectItem value="msp_tech">MSP Technician</SelectItem>
                            <SelectItem value="client_manager">Client Manager</SelectItem>
                            <SelectItem value="client_user">Client User</SelectItem>
                          </SelectContent>
                        </Select>
                        {user.id !== currentUser?.id && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleToggleUserStatus(user.id, user.is_active)}
                            className="border-red-200 text-red-600 hover:bg-red-50"
                          >
                            <UserX className="w-4 h-4 mr-1" />
                            Deactivate
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>

          {/* Pending Requests Tab */}
          <TabsContent value="pending" className="space-y-4">
            {accessRequests.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Clock className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p className="text-slate-500">No pending access requests</p>
                </CardContent>
              </Card>
            ) : (
              accessRequests.map((request) => (
                <Card key={request.id} className="border-yellow-200 bg-yellow-50">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="text-lg font-semibold text-slate-800">{request.requester_name}</h3>
                          <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">
                            Pending
                          </Badge>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-slate-600 mb-2">
                          <Mail className="w-4 h-4" />
                          {request.requester_email}
                        </div>
                        {request.message && (
                          <p className="text-sm text-slate-600 italic">"{request.message}"</p>
                        )}
                        <p className="text-xs text-slate-500 mt-2">
                          Requested: {new Date(request.created_at).toLocaleString()}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          onClick={() => openApproveDialog(request)}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          <UserCheck className="w-4 h-4 mr-1" />
                          Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleRejectRequest(request.id)}
                          className="border-red-200 text-red-600 hover:bg-red-50"
                        >
                          <UserX className="w-4 h-4 mr-1" />
                          Reject
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>

          {/* Contacts Tab */}
          <TabsContent value="contacts" className="space-y-4">
            {contacts.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <UserPlus className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p className="text-slate-500">No contacts available to convert</p>
                  <p className="text-xs text-slate-400 mt-2">
                    All contacts have already been converted to users
                  </p>
                </CardContent>
              </Card>
            ) : (
              contacts.map((contact) => (
                <Card key={contact.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="text-lg font-semibold text-slate-800">{contact.name}</h3>
                          <Badge variant="outline" className="text-xs">
                            {contact.client_name}
                          </Badge>
                          <Badge className="bg-slate-100 text-slate-600 border-slate-200">
                            Contact (Inactive User)
                          </Badge>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-slate-600 mb-1">
                          <Mail className="w-4 h-4" />
                          {contact.email}
                        </div>
                        {contact.role && (
                          <p className="text-sm text-slate-600">Role: {contact.role}</p>
                        )}
                        {contact.phone && (
                          <p className="text-sm text-slate-600">Phone: {contact.phone}</p>
                        )}
                      </div>
                      <Button
                        size="sm"
                        onClick={() => openConvertDialog(contact)}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        <UserPlus className="w-4 h-4 mr-1" />
                        Convert to User
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>

          {/* Roles & Permissions Tab */}
          <TabsContent value="roles" className="space-y-6">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h3 className="text-lg font-semibold">Configure Role Permissions</h3>
                <p className="text-sm text-slate-500">Control what each role can access in the system</p>
              </div>
              <Button
                variant="outline"
                onClick={handleResetPermissions}
                className="border-red-200 text-red-600 hover:bg-red-50"
              >
                Reset to Defaults
              </Button>
            </div>

            {Object.keys(rolePermissions).map((role) => (
              <Card key={role} className="overflow-hidden">
                <CardHeader className="bg-gradient-to-r from-slate-50 to-slate-100 border-b">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        role === 'msp_admin' ? 'bg-purple-100' :
                        role === 'msp_tech' ? 'bg-blue-100' :
                        role === 'client_manager' ? 'bg-green-100' : 'bg-gray-100'
                      }`}>
                        <Shield className={`w-5 h-5 ${
                          role === 'msp_admin' ? 'text-purple-600' :
                          role === 'msp_tech' ? 'text-blue-600' :
                          role === 'client_manager' ? 'text-green-600' : 'text-gray-600'
                        }`} />
                      </div>
                      <CardTitle>{getRoleLabel(role)}</CardTitle>
                    </div>
                    <Button
                      onClick={() => handleSaveRolePermissions(role)}
                      disabled={savingPermissions}
                      size="sm"
                    >
                      {savingPermissions ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Changes'}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="pt-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {/* Clients Module */}
                    <div className="space-y-3">
                      <h4 className="font-semibold text-sm text-slate-700 flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                        Clients
                      </h4>
                      <div className="space-y-2">
                        {['view', 'create', 'edit', 'delete'].map(action => (
                          <label key={action} className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={rolePermissions[role]?.clients?.[action] || false}
                              onChange={(e) => handlePermissionChange(role, 'clients', action, e.target.checked)}
                              className="rounded"
                            />
                            <span className="text-sm capitalize">{action}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Tickets Module */}
                    <div className="space-y-3">
                      <h4 className="font-semibold text-sm text-slate-700 flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
                        </svg>
                        Tickets
                      </h4>
                      <div className="space-y-2">
                        {['view', 'create', 'edit', 'delete'].map(action => (
                          <label key={action} className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={rolePermissions[role]?.tickets?.[action] || false}
                              onChange={(e) => handlePermissionChange(role, 'tickets', action, e.target.checked)}
                              className="rounded"
                            />
                            <span className="text-sm capitalize">{action}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Assets Module */}
                    <div className="space-y-3">
                      <h4 className="font-semibold text-sm text-slate-700 flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
                        </svg>
                        Assets
                      </h4>
                      <div className="space-y-2">
                        {['view', 'create', 'edit', 'delete'].map(action => (
                          <label key={action} className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={rolePermissions[role]?.assets?.[action] || false}
                              onChange={(e) => handlePermissionChange(role, 'assets', action, e.target.checked)}
                              className="rounded"
                            />
                            <span className="text-sm capitalize">{action}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Uptime Module */}
                    <div className="space-y-3">
                      <h4 className="font-semibold text-sm text-slate-700 flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                        </svg>
                        Uptime Monitor
                      </h4>
                      <div className="space-y-2">
                        {['view', 'create', 'edit', 'delete'].map(action => (
                          <label key={action} className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={rolePermissions[role]?.uptime?.[action] || false}
                              onChange={(e) => handlePermissionChange(role, 'uptime', action, e.target.checked)}
                              className="rounded"
                            />
                            <span className="text-sm capitalize">{action}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Knowledge Base Module */}
                    <div className="space-y-3">
                      <h4 className="font-semibold text-sm text-slate-700 flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                        </svg>
                        Knowledge Base
                      </h4>
                      <div className="space-y-2">
                        {['view', 'create', 'edit', 'delete'].map(action => (
                          <label key={action} className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={rolePermissions[role]?.knowledge?.[action] || false}
                              onChange={(e) => handlePermissionChange(role, 'knowledge', action, e.target.checked)}
                              className="rounded"
                            />
                            <span className="text-sm capitalize">{action}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Settings Module */}
                    <div className="space-y-3">
                      <h4 className="font-semibold text-sm text-slate-700 flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        Settings
                      </h4>
                      <div className="space-y-2">
                        {['view', 'edit'].map(action => (
                          <label key={action} className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={rolePermissions[role]?.settings?.[action] || false}
                              onChange={(e) => handlePermissionChange(role, 'settings', action, e.target.checked)}
                              className="rounded"
                            />
                            <span className="text-sm capitalize">{action}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Users Module */}
                    <div className="space-y-3">
                      <h4 className="font-semibold text-sm text-slate-700 flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                        </svg>
                        User Management
                      </h4>
                      <div className="space-y-2">
                        {['view', 'create', 'edit', 'delete'].map(action => (
                          <label key={action} className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={rolePermissions[role]?.users?.[action] || false}
                              onChange={(e) => handlePermissionChange(role, 'users', action, e.target.checked)}
                              className="rounded"
                            />
                            <span className="text-sm capitalize">{action}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Billing Module */}
                    <div className="space-y-3">
                      <h4 className="font-semibold text-sm text-slate-700 flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                        </svg>
                        Billing
                      </h4>
                      <div className="space-y-2">
                        {['view', 'edit'].map(action => (
                          <label key={action} className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={rolePermissions[role]?.billing?.[action] || false}
                              onChange={(e) => handlePermissionChange(role, 'billing', action, e.target.checked)}
                              className="rounded"
                            />
                            <span className="text-sm capitalize">{action}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </TabsContent>
        </Tabs>
      </div>

      {/* Approve Request Dialog */}
      <Dialog open={showApproveDialog} onOpenChange={setShowApproveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Approve Access Request</DialogTitle>
            <DialogDescription>
              Set a password and role for {selectedRequest?.requester_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="approve-password">Password *</Label>
              <Input
                id="approve-password"
                type="password"
                placeholder="Enter password (min 6 characters)"
                value={approvePassword}
                onChange={(e) => setApprovePassword(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="approve-role">User Role</Label>
              <Select value={approveRole} onValueChange={setApproveRole}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="msp_admin">MSP Administrator</SelectItem>
                  <SelectItem value="msp_tech">MSP Technician</SelectItem>
                  <SelectItem value="client_manager">Client Manager</SelectItem>
                  <SelectItem value="client_user">Client User</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowApproveDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleApproveRequest} disabled={processing}>
              {processing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Approving...
                </>
              ) : (
                <>
                  <UserCheck className="w-4 h-4 mr-2" />
                  Approve
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Convert Contact Dialog */}
      <Dialog open={showConvertDialog} onOpenChange={setShowConvertDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Convert Contact to User</DialogTitle>
            <DialogDescription>
              Create a user account for {selectedContact?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="convert-password">Password *</Label>
              <Input
                id="convert-password"
                type="password"
                placeholder="Enter password (min 6 characters)"
                value={convertPassword}
                onChange={(e) => setConvertPassword(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="convert-role">User Role</Label>
              <Select value={convertRole} onValueChange={setConvertRole}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="msp_admin">MSP Administrator</SelectItem>
                  <SelectItem value="msp_tech">MSP Technician</SelectItem>
                  <SelectItem value="client_manager">Client Manager</SelectItem>
                  <SelectItem value="client_user">Client User</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConvertDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleConvertContact} disabled={processing}>
              {processing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Converting...
                </>
              ) : (
                <>
                  <UserPlus className="w-4 h-4 mr-2" />
                  Convert
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UsersSettings;
